'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Message01Icon,
  ReplayIcon,
} from '@terra/ui-icons';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HttpEditor } from '../../http-editor';
import { Text } from '../../../components/text';
import { Tooltip } from '../../../components/tooltip';
import { IconButton } from '../../../components/icon-button';
import { useSendToReplay } from '../../chat/http-replay';
import { useSendHttpToChat } from '../../chat/http-send-to-chat';

type HttpExecutionCardProps = {
  requestPayload?: string;
  responsePayload?: string;
  error?: string;
  title?: string;
  headerActions?: ReactNode;
  afterReplayActions?: ReactNode;
  isResponsePending?: boolean;
  defaultResponseExpanded?: boolean;
  defaultRequestExpanded?: boolean;
};
const EMBEDDED_HTTP_EDITOR_MAX_HEIGHT = '300px';
const pendingResponseStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--colors-bg-muted) 25%, var(--colors-border-neutral-muted) 50%, var(--colors-bg-muted) 75%)',
  backgroundSize: '200% 100%',
};

export function HttpExecutionCard({
  requestPayload,
  responsePayload,
  error,
  title = 'HTTP',
  headerActions,
  afterReplayActions,
  isResponsePending,
  defaultResponseExpanded = false,
  defaultRequestExpanded = false,
}: HttpExecutionCardProps) {
  const t = useTranslations('designSystem.artifacts');
  const tChat = useTranslations('designSystem.chat');
  const tCommon = useTranslations('designSystem.common');
  const sendToReplay = useSendToReplay();
  const sendToChat = useSendHttpToChat();
  const [isResponseExpanded, setIsResponseExpanded] = useState(
    defaultResponseExpanded,
  );
  const [isRequestExpanded, setIsRequestExpanded] = useState(
    defaultRequestExpanded,
  );

  if (!requestPayload && !responsePayload && !error && !isResponsePending) {
    return null;
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={2}
        h="28px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {title}
        </Text>
        <HStack gap={1.5} alignItems="center">
          {headerActions}
          {sendToReplay && requestPayload ? (
            <Tooltip title={tChat('replayHttpRequest')}>
              <HStack
                as="button"
                aria-label={tChat('replayHttpRequest')}
                h="5"
                px={1}
                gap={1}
                alignItems="center"
                cursor="pointer"
                color="fg.muted"
                borderRadius="xs"
                onClick={() => sendToReplay(requestPayload)}
                _hover={{
                  color: 'fg.default',
                  bg: 'bg.subtle',
                }}
              >
                <ReplayIcon size={12} />
                <Text fontSize="xs" fontWeight="normal">
                  {tCommon('replay')}
                </Text>
              </HStack>
            </Tooltip>
          ) : null}
          {sendToChat && requestPayload ? (
            <Tooltip title={tChat('sendHttpToCopilot')}>
              <IconButton
                size="xs"
                variant="ghost"
                color="fg.muted"
                aria-label={tChat('sendHttpToCopilot')}
                onClick={() =>
                  sendToChat({
                    content: requestPayload,
                    responseContent: responsePayload,
                  })
                }
              >
                <Message01Icon size={12} />
              </IconButton>
            </Tooltip>
          ) : null}
          {afterReplayActions}
        </HStack>
      </HStack>
      {error ? (
        <Box
          px={2}
          py={2}
          borderBottomWidth={
            requestPayload || responsePayload || isResponsePending ? '1px' : '0'
          }
          borderColor="border.neutral.muted"
        >
          <Text fontSize="xs" color="fg.error" whiteSpace="pre-wrap">
            {error}
          </Text>
        </Box>
      ) : null}
      {requestPayload ? (
        <Box
          borderBottomWidth={responsePayload || isResponsePending ? '1px' : '0'}
          borderColor="border.neutral.muted"
        >
          <HStack
            px={2}
            h="28px"
            cursor="pointer"
            onClick={() => setIsRequestExpanded((prev) => !prev)}
            gap={1.5}
            alignItems="center"
          >
            {isRequestExpanded ? (
              <ArrowDown01Icon size={10} />
            ) : (
              <ArrowRight01Icon size={10} />
            )}
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('request')}
            </Text>
          </HStack>
          {isRequestExpanded ? (
            <HttpEditor
              value={requestPayload}
              readOnly
              hideToolbars
              height="auto"
              maxHeight={EMBEDDED_HTTP_EDITOR_MAX_HEIGHT}
            />
          ) : null}
        </Box>
      ) : null}
      {responsePayload ? (
        <Box>
          <HStack
            px={2}
            h="28px"
            cursor="pointer"
            onClick={() => setIsResponseExpanded((prev) => !prev)}
            gap={1.5}
            alignItems="center"
          >
            {isResponseExpanded ? (
              <ArrowDown01Icon size={10} />
            ) : (
              <ArrowRight01Icon size={10} />
            )}
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('response')}
            </Text>
          </HStack>
          {isResponseExpanded ? (
            <HttpEditor
              value={responsePayload}
              readOnly
              hideToolbars
              height="auto"
              maxHeight={EMBEDDED_HTTP_EDITOR_MAX_HEIGHT}
            />
          ) : null}
        </Box>
      ) : isResponsePending ? (
        <HStack px={2} h="28px" alignItems="center">
          <Box
            h="1.5"
            w="120px"
            borderRadius="full"
            animationStyle="shimmer"
            style={pendingResponseStyle}
          />
        </HStack>
      ) : null}
    </Box>
  );
}
